/* eslint-disable max-lines */
import { ComponentType } from "@angular/cdk/overlay";
import { Injectable } from "@angular/core";
import {
    ActivatedRoute,
    Params,
    Router,
} from "@angular/router";
import { Achievement } from "@Front-end/seed/__generated__";
import { CashierTypes } from "@Front-end/seed/models/payment";
import {
    UserBonus,
    UserViewStatuses,
} from "@Front-end/seed/models/user";
import { afterSuccessRegistration } from "@Front-end/seed/modules/quick-sign-up";
import { RegistrationAnalyticInitSources } from "@Front-end/seed/modules/registration-v2";
import {
    getUser,
    getUserBalance,
    UserFacade,
} from "@Front-end/seed/modules/user";
import { MissionItem } from "@Front-end/seed/modules/v2/missions";
import { ProjectParams } from "@Front-end/seed/utils";
import { ModalOpenType } from "@Front-end/ui/models/modal";
import { ModalService } from "@Front-end/ui/modules/modal";
import {
    Actions,
    createEffect,
    ofType,
} from "@ngrx/effects";
import { Store } from "@ngrx/store";
import {
    exhaustMap,
    filter,
    map,
    mergeMap,
    OperatorFunction,
    pipe,
    take,
    tap,
    withLatestFrom,
} from "rxjs";
import { CashierSources } from "../constants/cashier-source.constants";
import { CashierPayload } from "../models/cashier-payload.model";
import { RegistrationPopupPayload } from "../models/registration-payload.model";
import * as PopupsAction from "./app-popup.action";
import { ChangeBonusPayload } from "./model/change-bonus-payload.model";

@Injectable()
export class AppPopupsEffects {

    constructor(
        private actions$: Actions,
        private modalService: ModalService,
        private userService: UserFacade,
        private router: Router,
        private route: ActivatedRoute,
        private store: Store,
    ) {
    }

    // TODO use `true` to disable disableBackwardClose for all popups
    public readonly emergencyDisableBackwardClose: boolean = true;

    public openTermsAndConditions$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openTermsAndConditionsPopup),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ content, title }) => {
            const { TermsAndConditionsPopupComponent } = await import("../popups/terms-and-conditions/terms-and-conditions-popup.component");
            this.modalService.open(
                TermsAndConditionsPopupComponent,
                {
                    panelClass: "_terms-and-conditions",
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    data: {
                        title: title,
                        content: content,
                    },
                },
            );
        }),
    ), { dispatch: false });

    public openLogin$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openLoginPopup),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async () => {
            const { LoginPopupComponent } = await import("../popups/login/login-popup.component");
            this.modalService.open(
                LoginPopupComponent,
                {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    panelClass: "_login",
                },
            );
        }),
    ), { dispatch: false });

    public openRegistration$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openRegistration),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ payload }: { payload: RegistrationPopupPayload | null }) => {
                const { NewRegistrationPopupComponent } = await import("../popups/quick-registration/components/new-registration-popup/new-registration-popup.component");
                this.openRegistration(NewRegistrationPopupComponent, payload);
            }),
        ), { dispatch: false },
    );

    public openRewardBonus$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openUserBonus),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ bonuses }: { bonuses: UserBonus[] }) => {
                const { BonusesPopupComponent } = await import("../popups/bonuses/bonuses-popup.component");
                this.modalService.open(
                    BonusesPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        data: bonuses,
                        openType: ModalOpenType.PROGRAM,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openSearch$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openSearch),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async () => {
                const { SearchPopupComponent } = await import("../popups/search/search-popup.component");
                this.modalService.open(SearchPopupComponent, {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                });
            }),
        ), { dispatch: false },
    );

    public openLuckyWheel$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openLuckyWheel),
            exhaustMap(() => this.userService.getUserLoadingStatus().pipe(
                filter((status: UserViewStatuses) =>
                    status === UserViewStatuses.LOADED ||
                    status === UserViewStatuses.NO_USER,
                ),
                take(1),
                mergeMap(async (status: UserViewStatuses) => {
                    if (status === UserViewStatuses.LOADED) {
                        const { WheelPopupComponent } = await import("../popups/wheel/wheel-popup.component");
                        this.modalService.open(WheelPopupComponent, {
                            disableBackwardClose: this.emergencyDisableBackwardClose,
                            panelClass: "_lucky-wheel",
                            locked: true,
                        });
                    } else {
                        const { NewRegistrationPopupComponent } = await import("../popups/quick-registration/components/new-registration-popup/new-registration-popup.component");
                        this.openRegistration(
                            NewRegistrationPopupComponent,
                            {
                                source: RegistrationAnalyticInitSources.LUCKY_WIN_ATTEMPT,
                            },
                        );
                    }
                }),
            )),
        ), { dispatch: false },
    );

    public openLuckyResult$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openLuckyResult),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ payload }) => {
                const { LuckyResultPopupComponent } = await import("../popups/lucky-result/lucky-result-popup.component");
                this.modalService.open(LuckyResultPopupComponent, {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    data: payload,
                    panelClass: "_lucky-result",
                    locked: true,
                });
            }),
        ), { dispatch: false },
    );

    public openAllProviders$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openAllProviders),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async () => {
                const { AllProvidersPopupComponent } = await import("../popups/all-providers/all-providers-popup.component");
                this.modalService.open(
                    AllProvidersPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public registrationCashier$ = createEffect(() => this.actions$.pipe(
            ofType(afterSuccessRegistration),
            map(() => {
                const params: Params = this.route.snapshot.queryParams;
                const promocode: string | undefined = params[ProjectParams.PROMOCODE_CASHIER];
                return PopupsAction.openCashier({
                    payload: {
                        source: CashierSources.REGISTRATION,
                        type: CashierTypes.DEPOSIT,
                        openType: ModalOpenType.PROGRAM,
                        promoCode: promocode,
                    },
                });
            }),
        ),
    );

    public openCashier$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openCashier),
            withLatestFrom(this.userService.getUserLoadingStatus()),
            filter(([, status]: [{ payload: CashierPayload }, UserViewStatuses]) =>
                status === UserViewStatuses.LOADED ||
                status === UserViewStatuses.NO_USER,
            ),
            map(async ([{ payload }, status]: [{ payload: CashierPayload }, UserViewStatuses]) => {
                if (status === UserViewStatuses.LOADED) {
                    const { CashierPopupComponent } = await import("../popups/cashier/components/cashier-popup/cashier-popup.component");
                    this.modalService.open(
                        CashierPopupComponent,
                        {
                            disableBackwardClose: this.emergencyDisableBackwardClose,
                            disableClose: true,
                            panelClass: "_cashier",
                            data: payload,
                            openType: payload.openType || ModalOpenType.MANUAL,
                        });
                } else {
                    const { NewRegistrationPopupComponent } = await import("../popups/quick-registration/components/new-registration-popup/new-registration-popup.component");
                    this.openRegistration(NewRegistrationPopupComponent, {
                        ...payload,
                        source: RegistrationAnalyticInitSources.CASHIER_ATTEMPT,
                    });
                }
            }),
        ), { dispatch: false },
    );

    public openRecoveryPassword$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openRecoveryPassword),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async () => {
                const { RecoveryPasswordPopupComponent } = await import("../popups/recovery-password/recovery-password-popup.component");
                this.modalService.open(
                    RecoveryPasswordPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        panelClass: "_recovery", disableClose: true,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openChangeBonus$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openChangeBonus),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ payload }: { payload: ChangeBonusPayload }) => {
                const { ChangeBonusComponent } = await import("../popups/change-bonus/change-bonus-popup/change-bonus.component");
                this.modalService.open(
                    ChangeBonusComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        panelClass: "_change-bonus",
                        data: payload,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openPromoSuccess$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openPromoSuccess),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ userBonus }) => {
                const { PromoSuccessComponent } = await import("../popups/promo-success/promo-success.component");
                this.modalService.open(
                    PromoSuccessComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        data: userBonus,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openConfirmPhonePopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openConfirmPhonePopup),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ phone }) => {
                const { ConfirmPhonePopupComponent } = await import("../popups/confirm-phone/confirm-phone-popup.component");
                this.modalService.open(
                    ConfirmPhonePopupComponent,
                    {
                        panelClass: "_confirm",
                        data: phone,
                        disableClose: true,
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openBonusCancelConfirmPhonePopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openCancelBonusPopup),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ bonusId }) => {
                const { BonusCancelPopupComponent } = await import("../popups/bonus-cancel/bonus-cancel-popup.component");
                this.modalService.open(
                    BonusCancelPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        panelClass: "_recovery-result",
                        data: bonusId,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openFirstDepositBonusPopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openFirstDepositBonusPopup),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ bonus }) => {
                const { FirstDepositBonusPopupComponent } = await import("../popups/first-deposit-bonus/first-deposit-bonus-popup.component");
                this.modalService.open(
                    FirstDepositBonusPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        panelClass: "_recovery-result",
                        data: bonus,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openNeedPlayPopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.needPlay),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async () => {
                const { NeedPlayPopupComponent } = await import("../popups/need-play/need-play-popup.component");
                this.modalService.open(
                    NeedPlayPopupComponent,
                    {
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        panelClass: "_recovery-result",
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openNoBonusMoneyPopup$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openNoBonusMoneyPopup),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ isSport, isRedirectClose, gameAlias }) => {
            const { NoBonusMoneyPopupComponent } = await import("../popups/no-bonus-money/no-bonus-money-popup.component");
            this.modalService.open(
                NoBonusMoneyPopupComponent,
                {
                    data: { isSport, isRedirectClose, gameAlias },
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    panelClass: "_no-bonus-money",
                    disableClose: true,
                    openType: ModalOpenType.PROGRAM,
                    locked: true,
                },
            );
        }),
    ), { dispatch: false });

    public openChoseLangPopup$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openChooseLangsPopup),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ currentLanguage, langs }) => {
            const { ChooseLangsPopupComponent } = await import("../popups/choose-langs/choose-langs-popup.component");
            this.modalService.open(
                ChooseLangsPopupComponent,
                {
                    data: { currentLanguage, langs },
                    disableBackwardClose: true,
                },
            );
        }),
    ), { dispatch: false });

    public openConfirmChangeLimitPopup$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openConfirmChangeLimitPopup),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ limits, limitFormValue, deleteLimit }) => {
            const { ConfirmChangeLimitComponent } = await import("../popups/confirm-change-limit/confirm-change-limit.component");
            this.modalService.open(ConfirmChangeLimitComponent, {
                disableBackwardClose: this.emergencyDisableBackwardClose,
                data: { limits, limitFormValue, deleteLimit },
                panelClass: "_confirm-change-limit",
            });
        }),
    ), { dispatch: false });

    public openBuyItem$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openBuyItem),
        this.restrictOnGame(),
        withLatestFrom(
            this.store.select(getUser),
            this.store.select(getUserBalance),
        ),
        map(async ([{ payload: { choice, itemId, itemType } }, user, balance]) => {
            const isEnough: boolean = !!choice && !!balance?.loyalty && balance.loyalty >= choice.price;
            if (user !== null && isEnough) {
                const { BuyItemComponent } = await import("../popups/buy-item/buy-item.component");
                this.modalService.open(
                    BuyItemComponent,
                    {
                        data: { choice, itemId, itemType },
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                    },
                );
            }
            if (user === null) {
                const { NewRegistrationPopupComponent } = await import("../popups/quick-registration/components/new-registration-popup/new-registration-popup.component");
                this.openRegistration(
                    NewRegistrationPopupComponent,
                    {
                        source: RegistrationAnalyticInitSources.SHOP_BUY_ATTEMPT,
                    },
                );
            }
            if (user !== null && !isEnough && balance?.loyalty !== undefined && choice) {
                const { NotEnoughCoinsComponent } = await import("../popups/not-enough-coins/not-enough-coins.component");
                this.modalService.open(NotEnoughCoinsComponent, {
                    panelClass: "_not-enough",
                    data: {
                        existingCoins: balance.loyalty,
                        requiredCoins: choice.price,
                    },
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                });
            }
        }),
    ), { dispatch: false });

    public openAchievementDetails$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openAchievementDetails),
            this.restrictOnGame(),
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            tap(async ({ achievement, openType }: { achievement: Achievement, openType: ModalOpenType }) => {
                const { AchievementDetailsComponent } = await import("../popups/achievement-details/achievement-details.component");
                this.modalService.open(
                    AchievementDetailsComponent,
                    {
                        panelClass: "_achievement",
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        data: achievement,
                        openType,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openMissionDetails$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.openMissionDetails),
            this.restrictOnGame(),
            // eslint-disable-next-line
            tap(async ({ mission, openType }: { mission: MissionItem, openType: ModalOpenType }) => {
                const { MissionDetailsComponent } = await import("../popups/mission-details/mission-details.component");
                this.modalService.open(
                    MissionDetailsComponent,
                    {
                        panelClass: "_mission",
                        disableBackwardClose: this.emergencyDisableBackwardClose,
                        data: mission,
                        openType,
                    },
                );
            }),
        ), { dispatch: false },
    );

    public openConfirmTournamentJoinPopup$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openConfirmTournamentJoin),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ id, participationFee, currency }) => {
            const { ConfirmTournamentJoinComponent } = await import("../popups/confirm-tournament-join/confirm-tournament-join-popup.component");
            this.modalService.open(
                ConfirmTournamentJoinComponent,
                {
                    data: { id, participationFee, currency },
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    openType: ModalOpenType.MANUAL,
                },
            );
        }),
    ), { dispatch: false });

    public openNotEnoughMoneyPopup$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openNotEnoughMoney),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ currentAmount, requiredAmount, currency }) => {
            const { NotEnoughMoneyComponent } = await import("../popups/not-enough-money/not-enough-money-popup.component");
            this.modalService.open(
                NotEnoughMoneyComponent,
                {
                    data: { currentAmount, requiredAmount, currency },
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    openType: ModalOpenType.MANUAL,
                },
            );
        }),
    ), { dispatch: false });

    public closePopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.closePopup),
            tap((dialogResult: unknown) => this.modalService.close(dialogResult)),
        ), { dispatch: false },
    );

    public closeRedirectPopup$ = createEffect(() => this.actions$.pipe(
            ofType(PopupsAction.closeRedirectPopup),
            tap((dialogResult: unknown) => this.modalService.closeRedirect(dialogResult)),
        ), { dispatch: false },
    );

    public openEarnCoins$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openEarnCoins),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async () => {
            const { EarnCoinsComponent } = await import("../popups/earn-coins/earn-coins.component");
            this.modalService.open(
                EarnCoinsComponent,
                {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    panelClass: "_earn-coins",
                },
            );
        }),
    ), { dispatch: false });

    public openKycVerification$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openKycVerification),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async ({ typeKyc }) => {
            const { KycVerificationComponent } = await import("../popups/kyc-verification/kyc-verification.component");
            this.modalService.open(
                KycVerificationComponent,
                {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    panelClass: "_kyc-verification",
                    data: typeKyc,
                },
            );
        }),
    ), { dispatch: false });

    public openVerificationRequired$ = createEffect(() => this.actions$.pipe(
        ofType(PopupsAction.openVerificationRequired),
        this.restrictOnGame(),
        // eslint-disable-next-line @typescript-eslint/no-misused-promises
        tap(async () => {
            const { VerificationRequiredComponent } = await import("../popups/verification-required/verification-required.component");
            this.modalService.open(
                VerificationRequiredComponent,
                {
                    disableBackwardClose: this.emergencyDisableBackwardClose,
                    panelClass: "_verification-required",
                },
            );
        }),
    ), { dispatch: false });

    private restrictOnGame<T extends { openType: ModalOpenType }>(): OperatorFunction<T, T> {
        return pipe(
            filter((data: T): boolean => {
                const isGameRoute: boolean = this.router.url.includes("/game/");
                const isManualOpen: boolean = data.openType === ModalOpenType.MANUAL;
                return isGameRoute ? isManualOpen : true;
            }),
        );
    }

    private openRegistration(component: ComponentType<unknown>, payload: RegistrationPopupPayload | null = null): void {
        this.modalService.open(
            component,
            {
                disableBackwardClose: this.emergencyDisableBackwardClose,
                panelClass: "_registration",
                disableClose: true,
                data: payload,
            },
        );
    }

    private openNewRegistration(component: ComponentType<unknown>, payload: RegistrationPopupPayload | null = null): void {
        this.modalService.open(
            component,
            {
                disableBackwardClose: this.emergencyDisableBackwardClose,
                panelClass: "_registration",
                disableClose: true,
                data: payload,
            },
        );
    }
}
